/**
 * BumpContourTool — repulsion-based segmentation contour refinement.
 *
 * The tool is defined as a factory function instead of a static class because
 * @cornerstonejs/tools is loaded dynamically in dicomLoader.ts.  The factory
 * receives the already-initialised modules so BaseTool can be extended at
 * runtime without static imports.
 *
 * Algorithm
 * ---------
 * The brush circle acts as a physical repellent — contour points can never
 * exist inside it.  On every mouse-drag event the tool:
 *
 *   1. Finds all PlanarFreehandContourSegmentationTool annotations on the
 *      current slice (matched via referencedImageId).
 *   2. Converts each polyline point from world → canvas coordinates so that
 *      distance comparisons happen in screen pixels (radius is screen-pixel
 *      based so behaviour is zoom-independent).
 *   3. For each point closer to the brush centre than the radius:
 *        - Compute the unit vector from brush centre → point.
 *        - Snap the point outward to sit exactly on the circumference:
 *            new_canvas = brush_centre + unit_vector × radius
 *        Points on or outside the circumference are untouched.
 *   4. Self-intersections are resolved via split-and-discard: when two
 *      non-adjacent segments cross, the polygon is split into two loops
 *      at the intersection point, and the smaller loop (interior artifact)
 *      is discarded.  This repeats until intersection-free.
 *   5. Thin peninsulas (fold-backs where non-adjacent points nearly touch)
 *      are detected and collapsed when the enclosed sub-loop area is small.
 *   6. Near-duplicate consecutive points (< 0.5px apart) are removed.
 *   7. Only this viewport is re-rendered (not the full engine) to avoid
 *      disturbing the window/level of other viewports.
 *
 * Radius is sourced from this.configuration.radius (set synchronously via
 * toolGroup.setToolConfiguration) with a module-level variable as fallback.
 */

import {
  hasNnInteractiveSession,
  refreshViewerFromAnnotations,
  showNnToast,
} from "./nnInteractiveSession";
import { pushUndo } from "./annotationUndo";
import { logger } from "@/utils/logger";
import { getDefaultStore } from "jotai";
import { isSegmentationActiveAtom } from "../atoms/segmentationAtoms";

// Module-level radius fallback — updated by React via setBumpRadius().
let _bumpRadius = 28;
// Active segment index — bump only edits contours belonging to this segment.
let _activeSegmentIndex = 1;

/** Update the module-level radius fallback (synced from bumpRadiusAtom by
 *  VolumeViewport's useEffect). */
export function setBumpRadius(radius: number): void {
  _bumpRadius = radius;
}

/** Update which segment the bump tool is allowed to edit. */
export function setBumpSegmentIndex(index: number): void {
  _activeSegmentIndex = index;
}

/** Cosine threshold above which two viewPlaneNormals are considered the same
 *  orientation. cos(15°) ≈ 0.966 — well above any expected camera drift but
 *  rejects perpendicular planes (axial vs sagittal cos = 0). */
const NORMAL_MATCH_COS = 0.95;

type DicomMetadataGetter = (moduleName: string, imageId: string) => any;

export type DicomImageReferenceMatch = "same" | "different" | "spatial-fallback";

/**
 * Compare two Cornerstone image references without allowing same-position
 * images from different phases/series to alias one another.
 *
 * SOP identity is authoritative when both sides provide it. IPP is only a
 * fallback after the images prove spatially compatible through a shared
 * SeriesInstanceUID or FrameOfReferenceUID.
 */
export function classifyDicomImageReference(
  annotationImageId: string,
  currentImageId: string,
  getMetadata: DicomMetadataGetter,
  annotationFrameOfReferenceUid?: string,
  currentFrameOfReferenceUid?: string,
): DicomImageReferenceMatch {
  if (annotationImageId === currentImageId) return "same";

  const annotationImage = getMetadata("generalImageModule", annotationImageId);
  const currentImage = getMetadata("generalImageModule", currentImageId);
  const annotationSop = annotationImage?.sopInstanceUID as string | undefined;
  const currentSop = currentImage?.sopInstanceUID as string | undefined;
  if (annotationSop && currentSop) return annotationSop === currentSop ? "same" : "different";

  const annotationSeries = getMetadata("generalSeriesModule", annotationImageId)
    ?.seriesInstanceUID as string | undefined;
  const currentSeries = getMetadata("generalSeriesModule", currentImageId)?.seriesInstanceUID as
    | string
    | undefined;
  const annotationPlane = getMetadata("imagePlaneModule", annotationImageId);
  const currentPlane = getMetadata("imagePlaneModule", currentImageId);
  const annotationFor =
    annotationFrameOfReferenceUid ?? (annotationPlane?.frameOfReferenceUID as string | undefined);
  const currentFor =
    currentFrameOfReferenceUid ?? (currentPlane?.frameOfReferenceUID as string | undefined);
  const compatibleSeries =
    !!annotationSeries && !!currentSeries && annotationSeries === currentSeries;
  const compatibleFrame = !!annotationFor && !!currentFor && annotationFor === currentFor;
  if (!compatibleSeries && !compatibleFrame) return "different";

  const annotationIPP = annotationPlane?.imagePositionPatient as
    | [number, number, number]
    | undefined;
  const currentIPP = currentPlane?.imagePositionPatient as [number, number, number] | undefined;
  if (annotationIPP && currentIPP) {
    const distance = Math.hypot(
      annotationIPP[0] - currentIPP[0],
      annotationIPP[1] - currentIPP[1],
      annotationIPP[2] - currentIPP[2],
    );
    return distance < 0.01 ? "same" : "different";
  }

  return "spatial-fallback";
}

/** True when the annotation was drawn on a plane parallel to this viewport's
 *  current plane. We deliberately do NOT check camera-plane distance: bump
 *  operates in canvas space and the brush only reaches polylines that project
 *  to (X, Y) under the cursor, so an off-slice polyline that's nowhere near
 *  the brush is already filtered by geometry. Restricting to same-orientation
 *  blocks the cross-plane case (axial polylines on a coronal viewport — they'd
 *  project to a vertical line and bump would smear them) without preventing
 *  the user from editing a contour they can see on the companion plane just
 *  because nnInteractive parked the polyline on the centroid slice. */
function isAnnotationOnCurrentPlane(ann: any, viewport: any): boolean {
  try {
    const annNormal = ann?.metadata?.viewPlaneNormal as [number, number, number] | undefined;
    if (!annNormal) return true; // No stored normal — defer to canvas-distance gate below.
    const vpNormal = viewport?.getCamera?.()?.viewPlaneNormal as
      | [number, number, number]
      | undefined;
    if (!vpNormal) return true;
    const dot =
      vpNormal[0] * annNormal[0] + vpNormal[1] * annNormal[1] + vpNormal[2] * annNormal[2];
    return Math.abs(dot) >= NORMAL_MATCH_COS;
  } catch {
    return true;
  }
}

/** Tracks which slice imageIds have been modified by the bump tool during the current annotation. */
const _bumpedImageIds = new Set<string>();

/** Per-drag tracker: annotationUID → referencedImageId for every contour the
 *  current drag actually deformed. Used by mouseUp so cleanupPolyline only
 *  runs on the touched contours (not every same-plane same-segment annotation
 *  in the volume), and by _trackBump so the bumped-slice payload records the
 *  contour's own slice — not the viewport's parked slice, which can differ
 *  for cross-slice bumps (companion centroid case). Cleared on each
 *  preMouseDownCallback so a new drag starts with a clean set. */
const _dragMutatedAnnotations = new Map<string, string | undefined>();

/** Pre-bump polyline snapshots (deep copies) of the active-segment contours,
 *  captured at drag start (preMouseDownCallback) so Ctrl+Z (annotationUndo) can
 *  revert a bump edit by restoring the contour's geometry instead of deleting
 *  it. Keyed by annotationUID; cleared + repopulated on each drag. */
const _preBumpPolylines = new Map<string, number[][]>();

/** Throttled "no contour to refine" toast state. Without this, clicking Bump
 *  before drawing any contour silently does nothing (the filter rejects 0
 *  annotations and returns), which users reported as "bump is broken". */
let _lastNoContourWarn = 0;
const NO_CONTOUR_WARN_THROTTLE_MS = 3000;

function maybeShowNoContourToast(): void {
  const now = Date.now();
  if (now - _lastNoContourWarn < NO_CONTOUR_WARN_THROTTLE_MS) return;
  _lastNoContourWarn = now;
  showNnToast("No contour to refine on this slice — draw or region-grow first");
}

/** Record that the bump tool was used on a slice. Called internally by mouseDragCallback. */
function _trackBump(imageId: string): void {
  if (imageId) _bumpedImageIds.add(imageId);
}

/** Get all imageIds that were bumped since the last reset. */
export function getBumpedImageIds(): Set<string> {
  return new Set(_bumpedImageIds);
}

/** Reset bump tracking (call when starting a new annotation or after save). */
export function resetBumpTracking(): void {
  _bumpedImageIds.clear();
}

/** Drop a single imageId from the bump tracking set. Used when a
 *  single-slice contour is deleted so the save payload no longer
 *  reports that slice as bump-edited. */
export function removeBumpedImageId(imageId: string): void {
  if (imageId) _bumpedImageIds.delete(imageId);
}

/** Record that the bump tool edited `imageId`. Exposed for tests; production
 *  paths add via the module-local `_trackBump` from inside mouseDragCallback. */
export function addBumpedImageId(imageId: string): void {
  _trackBump(imageId);
}

/** Check if an annotation belongs to the currently active segment. */
function isActiveSegment(ann: any): boolean {
  return (
    ann.data?.segmentation?.segmentIndex === _activeSegmentIndex ||
    ann.data?.segmentIndex === _activeSegmentIndex
  );
}

// ─── Geometry helpers ─────────────────────────────────────────────────────────

type Pt2 = [number, number];
type Pt3 = [number, number, number];

/**
 * Returns the intersection parameter (t, u) if segment a1→a2 properly crosses
 * b1→b2, or null if they don't cross.
 */
function segmentIntersection(a1: Pt2, a2: Pt2, b1: Pt2, b2: Pt2): { t: number; u: number } | null {
  const dx1 = a2[0] - a1[0],
    dy1 = a2[1] - a1[1];
  const dx2 = b2[0] - b1[0],
    dy2 = b2[1] - b1[1];
  const denom = dx1 * dy2 - dy1 * dx2;
  if (Math.abs(denom) < 1e-10) return null;
  const ox = b1[0] - a1[0],
    oy = b1[1] - a1[1];
  const t = (ox * dy2 - oy * dx2) / denom;
  const u = (ox * dy1 - oy * dx1) / denom;
  if (t > 0.001 && t < 0.999 && u > 0.001 && u < 0.999) {
    return { t, u };
  }
  return null;
}

/** Linearly interpolate between two canvas points. */
function lerpCanvas(a: Pt2, b: Pt2, t: number): Pt2 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/** Signed area of a closed polygon in canvas space (shoelace formula). */
function signedArea(pts: Pt2[]): number {
  let area = 0;
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += pts[i][0] * pts[j][1];
    area -= pts[j][0] * pts[i][1];
  }
  return area / 2;
}

/** Ray-casting point-in-polygon test in 2D canvas space. */
function isPointInsidePolygon(point: Pt2, polygon: Pt2[]): boolean {
  let inside = false;
  const n = polygon.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i][0],
      yi = polygon[i][1];
    const xj = polygon[j][0],
      yj = polygon[j][1];
    if (
      yi > point[1] !== yj > point[1] &&
      point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Returns true if the majority (>80%) of `inner` polygon points lie inside
 * `outer`.  Used to detect a contour drawn entirely within an existing one.
 */
export function isPolygonInsidePolygon(inner: Pt2[], outer: Pt2[]): boolean {
  if (!inner.length || !outer.length) return false;
  let insideCount = 0;
  for (const pt of inner) {
    if (isPointInsidePolygon(pt, outer)) insideCount++;
  }
  return insideCount > inner.length * 0.8;
}

// ─── Post-bump cleanup pipeline ──────────────────────────────────────────────

/**
 * Removes self-intersections via split-and-discard.
 *
 * When non-adjacent segments i→(i+1) and j→(j+1) cross at point P, the
 * polygon is split into two loops:
 *   Loop A: [0..i, P, j+1..end]   (outer boundary)
 *   Loop B: [i+1..j, P]           (interior artifact)
 *
 * The loop with the larger absolute area is kept; the smaller one is
 * discarded.  Repeats until no crossings remain (capped at 30 passes).
 */
function resolveIntersections(
  world: Pt3[],
  canvas: Pt2[],
  viewport: any,
): { world: Pt3[]; canvas: Pt2[] } {
  let w = [...world];
  let c = [...canvas];

  let changed = true;
  let iterations = 0;

  while (changed && iterations < 5) {
    changed = false;
    iterations++;
    const len = w.length;
    if (len < 4) break;

    outer: for (let i = 0; i < len; i++) {
      const iNext = (i + 1) % len;
      for (let j = i + 2; j < len; j++) {
        const jNext = (j + 1) % len;
        if (jNext === i) continue;

        const hit = segmentIntersection(c[i], c[iNext], c[j], c[jNext]);
        if (!hit) continue;

        const crossCanvas = lerpCanvas(c[i], c[iNext], hit.t);
        const crossWorld = viewport.canvasToWorld(crossCanvas) as Pt3;

        const loopAWorld: Pt3[] = [...w.slice(0, i + 1), crossWorld, ...w.slice(j + 1)];
        const loopACanvas: Pt2[] = [...c.slice(0, i + 1), crossCanvas, ...c.slice(j + 1)];

        const loopBWorld: Pt3[] = [crossWorld, ...w.slice(i + 1, j + 1)];
        const loopBCanvas: Pt2[] = [crossCanvas, ...c.slice(i + 1, j + 1)];

        // Only discard the smaller loop if it's truly small relative to the
        // larger one — avoid discarding legitimate geometry.
        const areaA = Math.abs(signedArea(loopACanvas));
        const areaB = Math.abs(signedArea(loopBCanvas));
        const smaller = Math.min(areaA, areaB);
        const larger = Math.max(areaA, areaB);

        // Skip if the "smaller" loop is more than 20% of the larger — likely
        // both are real geometry, not a self-intersection artifact.
        if (smaller > larger * 0.2) continue;

        if (areaA >= areaB) {
          w = loopAWorld;
          c = loopACanvas;
        } else {
          w = loopBWorld;
          c = loopBCanvas;
        }

        changed = true;
        break outer;
      }
    }
  }

  return { world: w, canvas: c };
}

/**
 * Detects thin fold-backs (peninsulas) where two non-adjacent points are very
 * close in canvas space.  When the sub-loop they enclose has a small area
 * (< `areaThreshold` px²), the peninsula is collapsed by removing the points
 * between the two pinch-points.
 */
function removePinchPoints(
  world: Pt3[],
  canvas: Pt2[],
  pinchDist: number = 2,
  areaThreshold: number = 25,
): { world: Pt3[]; canvas: Pt2[] } {
  let w = [...world];
  let c = [...canvas];
  let changed = true;
  let iterations = 0;

  while (changed && iterations < 5) {
    changed = false;
    iterations++;
    const n = w.length;
    if (n < 6) break;

    outer: for (let i = 0; i < n; i++) {
      // Start j at i+3 so we skip immediate neighbors.
      for (let j = i + 3; j < n; j++) {
        // Skip near-wrap-around pairs.
        if (i === 0 && j >= n - 2) continue;

        const dx = c[i][0] - c[j][0];
        const dy = c[i][1] - c[j][1];
        if (dx * dx + dy * dy >= pinchDist * pinchDist) continue;

        // Found a pinch.  Compute the area of the sub-loop i..j.
        const subLoop = c.slice(i, j + 1);
        const subArea = Math.abs(signedArea(subLoop));
        if (subArea >= areaThreshold) continue;

        // The peninsula is small — collapse it by removing i+1..j-1.
        w = [...w.slice(0, i + 1), ...w.slice(j)];
        c = [...c.slice(0, i + 1), ...c.slice(j)];
        changed = true;
        break outer;
      }
    }
  }

  return { world: w, canvas: c };
}

/**
 * Aggressiveness of the bump/edit-tool tail+spike trim (Step 3 of cleanup).
 * Vertices whose two edges meet at an angle below this are cut. Higher = more
 * aggressive tail removal. Tune here if tails still show after an edit.
 */
const TAIL_TRIM_ANGLE_DEG = 15;

/**
 * Removes sharp spike tips / trailing tails where the polygon doubles back at an
 * acute angle. At each vertex, the angle between the two edges is computed. If
 * the angle is below `minAngleDeg`, the vertex is a spike/tail tip and is
 * removed. Iterates until no spikes remain.
 */
function removeSpikes(
  world: Pt3[],
  canvas: Pt2[],
  minAngleDeg: number,
): { world: Pt3[]; canvas: Pt2[] } {
  const cosThreshold = Math.cos((minAngleDeg * Math.PI) / 180);
  let w = [...world];
  let c = [...canvas];
  let changed = true;
  let iterations = 0;

  while (changed && iterations < 5) {
    changed = false;
    iterations++;
    const n = w.length;
    if (n < 4) break;

    for (let i = 0; i < n; i++) {
      const prev = (i - 1 + n) % n;
      const next = (i + 1) % n;

      // Vectors from vertex i toward its two neighbors.
      const dx1 = c[prev][0] - c[i][0],
        dy1 = c[prev][1] - c[i][1];
      const dx2 = c[next][0] - c[i][0],
        dy2 = c[next][1] - c[i][1];

      const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
      const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
      if (len1 < 0.001 || len2 < 0.001) continue;

      const cosAngle = (dx1 * dx2 + dy1 * dy2) / (len1 * len2);

      // cosAngle close to 1 → angle close to 0° → sharp spike tip.
      if (cosAngle > cosThreshold) {
        w.splice(i, 1);
        c.splice(i, 1);
        changed = true;
        break;
      }
    }
  }

  return { world: w, canvas: c };
}

/**
 * Removes consecutive points that are closer than `minDist` pixels in canvas
 * space.  Collapses degenerate near-coincident points.
 */
function deduplicatePoints(
  world: Pt3[],
  canvas: Pt2[],
  minDist: number = 0.5,
): { world: Pt3[]; canvas: Pt2[] } {
  if (world.length < 4) return { world, canvas };

  const outW: Pt3[] = [world[0]];
  const outC: Pt2[] = [canvas[0]];
  const minDist2 = minDist * minDist;

  for (let i = 1; i < world.length; i++) {
    const prev = outC[outC.length - 1];
    const cur = canvas[i];
    const dx = cur[0] - prev[0];
    const dy = cur[1] - prev[1];
    if (dx * dx + dy * dy >= minDist2) {
      outW.push(world[i]);
      outC.push(cur);
    }
  }

  // Wrap-around check: if last ≈ first, drop last.
  if (outC.length > 3) {
    const first = outC[0];
    const last = outC[outC.length - 1];
    const dx = last[0] - first[0];
    const dy = last[1] - first[1];
    if (dx * dx + dy * dy < minDist2) {
      outW.pop();
      outC.pop();
    }
  }

  return { world: outW, canvas: outC };
}

/**
 * Subdivides any segment longer than `maxGap` canvas-pixels so the contour
 * always has dense, evenly-spaced points.  Prevents long straight edges that
 * the bump tool can't smoothly deform.
 */
function densifyPolyline(world: Pt3[], canvas: Pt2[], viewport: any, maxGap: number = 6): Pt3[] {
  const dense: Pt3[] = [];
  const n = world.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    dense.push(world[i]);

    const ac = canvas[i];
    const bc = canvas[j];
    const segDx = bc[0] - ac[0];
    const segDy = bc[1] - ac[1];
    const segLen = Math.sqrt(segDx * segDx + segDy * segDy);

    if (segLen > maxGap) {
      const steps = Math.ceil(segLen / maxGap);
      for (let s = 1; s < steps; s++) {
        const t = s / steps;
        const interpCanvas: Pt2 = [ac[0] + t * segDx, ac[1] + t * segDy];
        dense.push(viewport.canvasToWorld(interpCanvas) as Pt3);
      }
    }
  }
  return dense;
}

/**
 * Full cleanup pipeline: resolve self-intersections → remove thin fold-backs
 * → deduplicate near-coincident points → densify long segments.
 *
 * Exported so other tools (e.g. RegionGrow, nnInteractive) can apply the
 * same pass to programmatically-built contours.
 *
 * @param mode  'bump' (default) runs the full aggressive pipeline.
 *              'initial' uses a light pass suitable for freshly-drawn contours
 *              where heavy intersection removal would discard valid geometry.
 */
function isFinite3(p: unknown): p is Pt3 {
  return (
    Array.isArray(p) &&
    p.length === 3 &&
    Number.isFinite(p[0]) &&
    Number.isFinite(p[1]) &&
    Number.isFinite(p[2])
  );
}

/** Throttled bypass log — emitted when the snap silently skips an annotation
 *  so production traces can identify legacy/missing-normal cases that still
 *  carry the #471 bend. */
let _lastSnapBypassWarn = 0;
const SNAP_BYPASS_WARN_THROTTLE_MS = 5000;
function warnSnapBypass(reason: string, extra?: Record<string, unknown>): void {
  const now = Date.now();
  if (now - _lastSnapBypassWarn < SNAP_BYPASS_WARN_THROTTLE_MS) return;
  _lastSnapBypassWarn = now;
  logger.warn("[BumpContourTool] snapPolylineToPlane bypassed — %s %o", reason, extra ?? {});
}

/**
 * Project every point of a polyline onto a single plane defined by `normal`
 * and an anchor point.
 *
 * Run on mouseUp to remove sub-millimetre out-of-plane drift the bump pipeline
 * introduces: bumped + subdivided + densified points come from
 * `viewport.canvasToWorld()` (camera focal plane) while untouched points
 * retain their original IPP-derived coordinates (slice plane). When the two
 * planes don't coincide exactly (typical for nnInteractive-generated contours
 * after any camera adjustment), the 3D viewer's `LineLoop` renders the bumped
 * arc at a visibly different Z than the rest of the ring — issue #471.
 *
 * **Anchor selection.** When `anchor` is supplied (caller passes the slice's
 * IPP from `imagePlaneModule.imagePositionPatient`), the snap plane is that
 * exact slice — preserves every IPP-derived original point exactly and pulls
 * only the canvasToWorld-derived points back. This is the preferred path: it
 * survives `snapCameraToCardinal` divergence (camera VPN snapped to a cardinal
 * axis while the slice's true `rowCos × colCos` stays oblique) and is robust
 * to `densifyPolyline` injecting many camera-plane interpolated points that
 * would flip a median-based anchor.
 *
 * When `anchor` is null/undefined (no IPM available — legacy contours, lasso
 * draws without a referenced image), the anchor falls back to the median dot
 * along the normal. Median (not mean) is dominated by the untouched majority,
 * so the resulting plane sits on the original drawing depth for typical edits.
 *
 * **Normal selection.** Caller supplies `normal`. Prefer the slice's
 * `rowCos × colCos` (the true acquisition normal) over `camera.viewPlaneNormal`
 * which may have been snapped to a cardinal axis for non-acquisition-aligned
 * volumes.
 *
 * **NaN safety.** Returns the input polyline unchanged when:
 *  - `normal` is missing or zero-length
 *  - `normal` or `anchor` has any non-finite component
 *  - `polyline.length < 3` (no plane is well-defined)
 *  - the polyline contains any non-finite point (a NaN in canvasToWorld near
 *    a viewport edge would otherwise poison the entire ring via the median)
 *  - the post-snap output has any non-finite point (defence in depth)
 *
 * Each bypass logs (throttled) so production has a positive monitoring signal.
 */
export function snapPolylineToPlane(
  polyline: Pt3[],
  normal: Pt3 | undefined | null,
  anchor?: Pt3 | null,
): Pt3[] {
  if (!normal) {
    warnSnapBypass("missing normal", { polylineLen: polyline.length });
    return polyline;
  }
  if (polyline.length < 3) {
    warnSnapBypass("polyline too short", { polylineLen: polyline.length });
    return polyline;
  }
  if (!isFinite3(normal)) {
    warnSnapBypass("non-finite normal", { normal });
    return polyline;
  }
  if (anchor != null && !isFinite3(anchor)) {
    warnSnapBypass("non-finite anchor", { anchor });
    return polyline;
  }

  const [nx, ny, nz] = normal;
  const nLenSq = nx * nx + ny * ny + nz * nz;
  if (nLenSq < 1e-12) {
    warnSnapBypass("zero-length normal", { normal });
    return polyline;
  }
  const nLen = Math.sqrt(nLenSq);
  const nxN = nx / nLen;
  const nyN = ny / nLen;
  const nzN = nz / nLen;

  // Reject up-front if the polyline contains any non-finite point — a single
  // NaN would otherwise propagate through dots → median → every output point.
  for (const p of polyline) {
    if (!isFinite3(p)) {
      warnSnapBypass("non-finite polyline point", { sample: p });
      return polyline;
    }
  }

  let anchorDot: number;
  if (anchor) {
    // Exact slice plane — the anchor projects to its own dot along the normal.
    anchorDot = anchor[0] * nxN + anchor[1] * nyN + anchor[2] * nzN;
  } else {
    // Median-along-normal fallback: dominated by the untouched majority, so
    // the resulting plane matches the contour's original drawing depth for
    // typical localized edits.
    const dots = polyline.map((p) => p[0] * nxN + p[1] * nyN + p[2] * nzN);
    const sorted = [...dots].sort((a, b) => a - b);
    const mid = sorted.length >> 1;
    anchorDot = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  }

  const snapped: Pt3[] = polyline.map((p): Pt3 => {
    const d = p[0] * nxN + p[1] * nyN + p[2] * nzN - anchorDot;
    return [p[0] - d * nxN, p[1] - d * nyN, p[2] - d * nzN];
  });

  // Defence in depth: if anything went non-finite, return the input.
  for (const p of snapped) {
    if (!isFinite3(p)) {
      warnSnapBypass("post-snap non-finite — discarding result", { sample: p });
      return polyline;
    }
  }
  return snapped;
}

export function cleanupPolyline(
  polyline: Pt3[],
  viewport: any,
  mode: "bump" | "initial" = "bump",
): Pt3[] {
  if (polyline.length < 4) return polyline;

  let canvas: Pt2[] = polyline.map((pt) => viewport.worldToCanvas(pt) as Pt2);
  let world = [...polyline];

  if (mode === "initial") {
    // Light cleanup for freshly-drawn contours:
    // 1. Deduplicate near-coincident points.
    // 2. Densify long segments.
    const deduped = deduplicatePoints(world, canvas);
    return densifyPolyline(deduped.world, deduped.canvas, viewport);
  }

  // Full pipeline for bump-tool modifications:

  // Step 1: split-and-discard self-intersections.
  const resolved = resolveIntersections(world, canvas, viewport);
  world = resolved.world;
  canvas = resolved.canvas;

  // Step 2: collapse thin peninsulas / fold-backs.
  const pinched = removePinchPoints(world, canvas);
  world = pinched.world;
  canvas = pinched.canvas;

  // Step 3: remove spike tips / trailing tails. The angle threshold is the
  // aggressiveness knob: vertices whose edges meet at an angle below this are
  // cut. Raised from the old 5° (which only caught near-degenerate spikes and
  // left visible tails) to 15° to trim tails more aggressively. Bump higher if
  // tails still show.
  const despiked = removeSpikes(world, canvas, TAIL_TRIM_ANGLE_DEG);
  world = despiked.world;
  canvas = despiked.canvas;

  // Step 4: remove near-duplicate consecutive points.
  const deduped = deduplicatePoints(world, canvas);
  world = deduped.world;
  canvas = deduped.canvas;

  // Step 5: re-densify — earlier steps may have removed points leaving long gaps.
  return densifyPolyline(world, canvas, viewport);
}

// ─── Tool factory ─────────────────────────────────────────────────────────────

/**
 * Returns the BumpContourTool class after Cornerstone modules are available.
 */
export function createBumpContourToolClass(cornerstone: any, cornerstoneTools: any): any {
  const { BaseTool, annotation: annotationModule } = cornerstoneTools;

  /**
   * Resolve the (normal, anchor) pair for snapping a polyline back onto the
   * slice it belongs to. Prefers the IPM-derived true acquisition normal
   * (rowCos × colCos) + IPP anchor over the annotation's stored
   * `viewPlaneNormal`, because the stored value comes from
   * `camera.viewPlaneNormal` which may have been snapped to a cardinal axis
   * by `snapCameraToCardinal` for mildly-oblique series — that snap leaves
   * the camera VPN and the IPM normal divergent, and a plane projection onto
   * the camera VPN would tilt the contour off its true slice. Falls back to
   * the stored normal + median anchor when no IPM is available (legacy data,
   * referenced image without imagePlaneModule).
   */
  function resolveSnapPlaneForAnnotation(ann: any): {
    normal: Pt3 | undefined;
    anchor: Pt3 | undefined;
  } {
    const refId = ann.metadata?.referencedImageId as string | undefined;
    if (refId) {
      try {
        const ipm = cornerstone.metaData?.get?.("imagePlaneModule", refId);
        const rc = ipm?.rowCosines as Pt3 | undefined;
        const cc = ipm?.columnCosines as Pt3 | undefined;
        const ipp = ipm?.imagePositionPatient as Pt3 | undefined;
        if (rc && cc && ipp) {
          const normal: Pt3 = [
            rc[1] * cc[2] - rc[2] * cc[1],
            rc[2] * cc[0] - rc[0] * cc[2],
            rc[0] * cc[1] - rc[1] * cc[0],
          ];
          return { normal, anchor: ipp };
        }
      } catch {
        // IPM lookup threw — fall through to the stored-normal path below.
      }
    }
    return {
      normal: ann.metadata?.viewPlaneNormal as Pt3 | undefined,
      anchor: undefined,
    };
  }
  const { getEnabledElement } = cornerstone;

  /**
   * Cornerstone can give the annotation and the viewport different image-id
   * strings for the same DICOM frame (for example after progressive loading
   * or a volume/stack strategy handoff).  Comparing the raw ids made Bump
   * report "no contour" on the very slice where Lasso just drew one.
   */
  const isAnnotationOnCurrentSlice = (
    ann: any,
    currentImageId: string | undefined,
    viewport: any,
  ): boolean => {
    const annotationImageId = ann?.metadata?.referencedImageId as string | undefined;
    if (!currentImageId || !annotationImageId || annotationImageId === currentImageId) return true;

    try {
      const referenceMatch = classifyDicomImageReference(
        annotationImageId,
        currentImageId,
        (moduleName, imageId) => cornerstone.metaData?.get?.(moduleName, imageId),
        ann?.metadata?.FrameOfReferenceUID,
        viewport?.getFrameOfReferenceUID?.(),
      );
      if (referenceMatch === "same") return true;
      if (referenceMatch === "different") return false;
    } catch {
      // Metadata failure cannot establish a compatible series or FoR.
      return false;
    }

    // Volume viewports can emit a referenced image id for which the metadata
    // provider has no entry even though the contour is rendered on the active
    // slice. Compare the contour plane directly with the camera focal plane.
    try {
      const polyline = ann?.data?.contour?.polyline as Pt3[] | undefined;
      const camera = viewport?.getCamera?.();
      const point = polyline?.[0];
      const focalPoint = camera?.focalPoint as Pt3 | undefined;
      const normal = camera?.viewPlaneNormal as Pt3 | undefined;
      if (point && focalPoint && normal) {
        const planeDistance = Math.abs(
          (point[0] - focalPoint[0]) * normal[0] +
            (point[1] - focalPoint[1]) * normal[1] +
            (point[2] - focalPoint[2]) * normal[2],
        );
        return planeDistance < 0.1;
      }
    } catch {
      // Retain the conservative false result for an unknown slice.
    }
    return false;
  };

  class BumpContourTool extends BaseTool {
    static toolName = "BumpContourTool";

    constructor(toolProps: any = {}, defaultToolProps: any = {}) {
      super(toolProps, {
        name: "BumpContourTool",
        bindings: [],
        ...defaultToolProps,
      });
    }

    /**
     * Consume the mouse-down event before Cornerstone's annotation handle
     * detection can intercept clicks near contour points and dispatch them
     * to the passive lasso tool instead of BumpContourTool.
     */
    preMouseDownCallback(evt: any): boolean {
      const { element } = evt.detail;
      const enabledElement = getEnabledElement(element);
      if (!enabledElement?.viewport) return false;
      // Fresh drag — reset the per-drag mutation tracker so mouseUp's cleanup
      // loop and bumped-slice tracking only see annotations this drag touched.
      _dragMutatedAnnotations.clear();
      // Snapshot the active-segment contours' polylines BEFORE any drag mutation
      // so mouseUp can register a bump-revert with the undo log (Ctrl+Z).
      _preBumpPolylines.clear();
      try {
        for (const ann of (annotationModule.state.getAllAnnotations() ?? []) as any[]) {
          if (
            ann.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
            isActiveSegment(ann) &&
            Array.isArray(ann.data?.contour?.polyline)
          ) {
            _preBumpPolylines.set(
              ann.annotationUID,
              ann.data.contour.polyline.map((p: number[]) => [p[0], p[1], p[2]]),
            );
          }
        }
      } catch (e) {
        // warn, not debug: an empty snapshot means no pushUndo on mouseUp, so
        // Ctrl+Z silently won't revert this bump. debug is a prod no-op, leaving
        // a lost-undo with zero signal — mirror the mouseUp catch below.
        logger.warn("[BumpContourTool] pre-bump snapshot failed — this bump won't be undoable:", e);
      }
      // Returning true tells Cornerstone this tool owns the interaction —
      // prevents fallthrough to passive annotation handle selection.
      return true;
    }

    mouseDragCallback(evt: any): void {
      const { element, deltaPoints, currentPoints } = evt.detail;

      const enabledElement = getEnabledElement(element);
      if (!enabledElement?.viewport) return;

      const viewport = enabledElement.viewport as any;

      const deltaCanvas: Pt2 = deltaPoints?.canvas ?? [0, 0];
      const mouseCanvas: Pt2 = currentPoints.canvas;

      if (Math.abs(deltaCanvas[0]) < 0.1 && Math.abs(deltaCanvas[1]) < 0.1) return;

      const allAnnotations: any[] = annotationModule.state.getAllAnnotations() ?? [];
      if (!allAnnotations.length) {
        maybeShowNoContourToast();
        return;
      }

      // Plane-match (viewPlaneNormal alignment) is required because
      // referencedImageId-only matching breaks on companion sagittal/coronal
      // viewports — their getCurrentImageId() returns an imageId from a
      // different series than the loaded contour's metadata.
      //
      // Current-slice gate: plane-match ALONE selects every active-segment
      // contour on this plane (all slices). The brush works in canvas space,
      // so contours on adjacent slices that project under the brush (the stone
      // sits at nearly the same x,y across slices) get reshaped too — bleeding
      // one bump onto neighbouring slices, including a committed AI seg. A bump
      // must only sculpt the slice the user is on, so additionally require the
      // contour to belong to the viewport's current image. When the viewport
      // can't report a current imageId we fall back to plane-match (no
      // regression vs the prior behavior) rather than silently bumping nothing.
      const currentImageId: string | undefined = viewport.getCurrentImageId?.();
      const candidates = allAnnotations.filter(
        (ann: any) =>
          ann.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
          isActiveSegment(ann) &&
          isAnnotationOnCurrentPlane(ann, viewport),
      );
      // A volume renderer chooses the displayed slice geometrically. Its image
      // reference can disagree with the visible contour in non-axial views.
      // Use the same filter as Lasso's renderer so Bump cannot edit an invisible
      // neighbouring contour merely because its image reference matches.
      const contourTool = getDefaultStore().get(isSegmentationActiveAtom)
        ? cornerstoneTools.ToolGroupManager?.getToolGroupForViewport(
            viewport.id,
            enabledElement.renderingEngineId,
          )?.getToolInstance("PlanarFreehandContourSegmentationTool")
        : undefined;
      const contourAnnotations = contourTool
        ? contourTool.filterInteractableAnnotationsForElement(element, candidates)
        : candidates.filter((ann: any) =>
            isAnnotationOnCurrentSlice(ann, currentImageId, viewport),
          );
      if (!contourAnnotations.length) {
        maybeShowNoContourToast();
        return;
      }

      const radius: number = (this as any).configuration?.radius ?? _bumpRadius;
      // Margin beyond the radius where we pre-subdivide so the brush has
      // intermediate points to push — covers the full falloff zone.
      const subdivMargin = radius * 1.8;
      let anyModified = false;

      for (const ann of contourAnnotations) {
        const polyline: Pt3[] = ann.data?.contour?.polyline;
        if (!polyline?.length) continue;

        // ── Pre-subdivide long segments that pass near the brush ──────────
        // For each segment, if the closest point on the segment to the brush
        // centre is within subdivMargin, insert intermediate points so the
        // bump pass can create a smooth curve instead of moving a rigid edge.
        const subdividedWorld: Pt3[] = [];
        const n = polyline.length;
        for (let i = 0; i < n; i++) {
          const a = polyline[i];
          const b = polyline[(i + 1) % n];
          subdividedWorld.push(a);

          const ac: Pt2 = viewport.worldToCanvas(a);
          const bc: Pt2 = viewport.worldToCanvas(b);

          // Segment length in canvas pixels
          const segDx = bc[0] - ac[0];
          const segDy = bc[1] - ac[1];
          const segLen = Math.sqrt(segDx * segDx + segDy * segDy);
          if (segLen < 4) continue; // already short enough

          // Closest point on segment to brush centre (parametric t, clamped)
          const tClosest = Math.max(
            0,
            Math.min(
              1,
              ((mouseCanvas[0] - ac[0]) * segDx + (mouseCanvas[1] - ac[1]) * segDy) /
                (segLen * segLen),
            ),
          );
          const cpx = ac[0] + tClosest * segDx;
          const cpy = ac[1] + tClosest * segDy;
          const closestDist = Math.sqrt((cpx - mouseCanvas[0]) ** 2 + (cpy - mouseCanvas[1]) ** 2);

          if (closestDist >= subdivMargin) continue; // segment is far from brush

          // Insert evenly-spaced intermediate points (~3px apart in canvas)
          const steps = Math.ceil(segLen / 3);
          for (let s = 1; s < steps; s++) {
            const t = s / steps;
            const interpCanvas: Pt2 = [ac[0] + t * segDx, ac[1] + t * segDy];
            subdividedWorld.push(viewport.canvasToWorld(interpCanvas) as Pt3);
          }
        }

        // ── Bump pass — only affect the nearest contour edge ─────────────
        // Find the closest point to the brush center, then walk outward in
        // both directions along the polygon to collect a contiguous arc of
        // points near the brush. Points on a far edge (separated by points
        // outside the influence zone) are never reached and stay untouched.
        let annModified = false;

        // Pre-compute canvas positions and distances
        const canvasPts: Pt2[] = subdividedWorld.map((pt) => viewport.worldToCanvas(pt) as Pt2);
        const dists: number[] = canvasPts.map((cp) => {
          const dx = cp[0] - mouseCanvas[0];
          const dy = cp[1] - mouseCanvas[1];
          return Math.sqrt(dx * dx + dy * dy);
        });

        // Find closest point to brush center
        let closestIdx = 0;
        let closestDist = dists[0];
        for (let i = 1; i < dists.length; i++) {
          if (dists[i] < closestDist) {
            closestDist = dists[i];
            closestIdx = i;
          }
        }

        // If the closest point is outside the radius, brush doesn't touch the contour
        if (closestDist >= radius) {
          // Still apply subdivision if we added points
          if (subdividedWorld.length !== polyline.length) {
            ann.data.contour.polyline = subdividedWorld;
            anyModified = true;
            if (ann.annotationUID) {
              _dragMutatedAnnotations.set(ann.annotationUID, ann.metadata?.referencedImageId);
            }
          }
          continue;
        }

        // Build set of eligible indices: walk from closest point in both
        // directions, stopping when we hit a point outside the influence zone.
        const influenceRadius = radius * 1.5;
        const eligible = new Set<number>();
        const ptsLen = subdividedWorld.length;

        // Walk forward
        for (let step = 0; step < ptsLen; step++) {
          const i = (closestIdx + step) % ptsLen;
          if (dists[i] < influenceRadius) eligible.add(i);
          else break;
        }
        // Walk backward
        for (let step = 1; step < ptsLen; step++) {
          const i = (closestIdx - step + ptsLen) % ptsLen;
          if (dists[i] < influenceRadius) eligible.add(i);
          else break;
        }

        const bumped = subdividedWorld.map((pt: Pt3, idx: number) => {
          if (!eligible.has(idx)) return pt;

          const dist = dists[idx];
          if (dist >= radius || dist < 0.001) return pt;

          const canvasPt = canvasPts[idx];
          const dx = canvasPt[0] - mouseCanvas[0];
          const dy = canvasPt[1] - mouseCanvas[1];
          const nx = dx / dist;
          const ny = dy / dist;
          const newCanvas: Pt2 = [mouseCanvas[0] + nx * radius, mouseCanvas[1] + ny * radius];

          annModified = true;
          return viewport.canvasToWorld(newCanvas) as Pt3;
        });

        if (annModified || subdividedWorld.length !== polyline.length) {
          // No cleanup during drag — dedup+densify on every event progressively
          // collapses the contour. Cleanup runs once on mouseUp instead.
          ann.data.contour.polyline = bumped;
          anyModified = true;
          if (ann.annotationUID) {
            // Track this annotation as mutated by the current drag — mouseUp's
            // cleanup loop iterates this map (not every same-plane contour),
            // and _trackBump records ann.metadata.referencedImageId (the slice
            // the contour actually lives on) instead of the viewport's parked
            // slice, which can differ for cross-slice bumps.
            _dragMutatedAnnotations.set(ann.annotationUID, ann.metadata?.referencedImageId);
          }
        }
      }

      if (anyModified) {
        try {
          viewport.render?.();
        } catch {
          // Viewport may be disposed during cleanup
        }
      }
    }

    mouseUpCallback(evt: any): void {
      try {
        const { element } = evt.detail;
        const enabledElement = getEnabledElement(element);
        if (!enabledElement?.viewport) return;
        const viewport = enabledElement.viewport as any;

        // On mouse-up run the full cleanup (intersection resolution etc.)
        // once, so any self-intersections from the drag session are resolved.
        // Iterate only annotations the drag actually mutated — running
        // cleanupPolyline on every same-plane same-segment contour would
        // silently rewrite contours the user didn't touch (e.g. propagated
        // neighbour slices) since cleanupPolyline is destructive
        // (resolveIntersections → dedup → densify).
        const allAnnotations: any[] = annotationModule.state.getAllAnnotations() ?? [];
        const mutatedUIDs = _dragMutatedAnnotations;

        let lastModified: any = null;
        for (const ann of allAnnotations) {
          if (!ann.annotationUID || !mutatedUIDs.has(ann.annotationUID)) continue;
          const polyline = ann.data?.contour?.polyline;
          if (!polyline || polyline.length < 4) continue;

          // Per-annotation try/catch so a throw on one annotation's cleanup
          // doesn't leave other mutated annotations with off-plane subdivisions
          // from mouseDragCallback. Without this, a viewport disposed
          // mid-mouseUp (layout reflow, route change) would swallow the snap
          // for every annotation in the loop and #471's bend would persist.
          try {
            const { normal, anchor } = resolveSnapPlaneForAnnotation(ann);
            const cleaned = cleanupPolyline(polyline, viewport, "bump");
            // Snap onto the slice's true plane to remove sub-mm out-of-plane
            // drift between IPP-derived original points and canvasToWorld-
            // derived bumped/inserted points (#471).
            ann.data.contour.polyline = snapPolylineToPlane(cleaned, normal, anchor);
            lastModified = ann;
          } catch (err) {
            // cleanupPolyline needs the viewport; snapPolylineToPlane does not.
            // If cleanup threw (e.g. disposed viewport), still attempt the
            // snap on the raw polyline so the contour at least leaves mouseUp
            // coplanar — otherwise mouseDragCallback's subdividedWorld write
            // persists with canvasToWorld off-plane points.
            try {
              const { normal, anchor } = resolveSnapPlaneForAnnotation(ann);
              ann.data.contour.polyline = snapPolylineToPlane(polyline, normal, anchor);
              lastModified = ann;
            } catch (err2) {
              logger.warn(
                "[BumpContourTool] snap fallback failed for annotation %s: %o",
                ann.annotationUID,
                err2,
              );
            }
            logger.warn(
              "[BumpContourTool] cleanupPolyline threw for annotation %s — snap-only fallback applied: %o",
              ann.annotationUID,
              err,
            );
          }

          // Record the bumped slice against the contour's own
          // referencedImageId — for cross-slice bumps this differs from the
          // viewport's parked slice, and the save payload's bumped_slices
          // set is keyed on the annotation's referencedImageId.
          const refId = mutatedUIDs.get(ann.annotationUID);
          if (refId) _trackBump(refId);
        }

        // Register a bump-revert with the undo log: Ctrl+Z restores the
        // pre-bump polylines of exactly the contours this drag deformed (no-op
        // for any UID that's since been removed). One bump drag = one undo step.
        const revertSnaps: Array<{ uid: string; polyline: number[][] }> = [];
        for (const uid of Array.from(mutatedUIDs.keys())) {
          const snap = _preBumpPolylines.get(uid);
          if (snap) revertSnaps.push({ uid, polyline: snap });
        }
        if (revertSnaps.length > 0) {
          pushUndo({
            kind: "bumpRevert",
            undo: () => {
              const byUid = new Map(
                ((annotationModule.state.getAllAnnotations() ?? []) as any[]).map((a) => [
                  a.annotationUID,
                  a,
                ]),
              );
              for (const { uid, polyline } of revertSnaps) {
                const a: any = byUid.get(uid);
                if (a?.data?.contour) {
                  a.data.contour.polyline = polyline.map((p) => [p[0], p[1], p[2]]);
                  // Mirror the commit path (below): re-fire ANNOTATION_MODIFIED
                  // so InterpolationManager re-interpolates from the restored
                  // (pre-bump) polyline. Without this, neighbour-keyframe
                  // contours the bump produced on commit are left stale and the
                  // undo isn't the true inverse of the bump. Skip region-grow
                  // contours (same guard as the commit) — they auto-propagate,
                  // and triggering InterpolationManager on them duplicates
                  // contours. The undo log is cleared on case change, so the
                  // captured `element` never outlives its study.
                  if (!a._regionGrow) {
                    try {
                      annotationModule.state.triggerAnnotationModified(a, element);
                    } catch (err) {
                      logger.warn(
                        "[BumpContourTool] bump-undo triggerAnnotationModified failed for %s: %o",
                        uid,
                        err,
                      );
                    }
                  }
                }
              }
            },
          });
        }
        mutatedUIDs.clear();

        viewport.render?.();

        // Fire ANNOTATION_MODIFIED so InterpolationManager re-interpolates
        // between keyframes after the bump edit — but only for non-region-grow
        // annotations.  Region-grow contours use auto-propagation instead of
        // geo-interpolation; triggering InterpolationManager on them creates
        // duplicate contours that conflict with the propagated ones.
        if (lastModified && !lastModified._regionGrow) {
          try {
            annotationModule.state.triggerAnnotationModified(lastModified, element);
          } catch (e) {
            logger.debug("[BumpContourTool] triggerAnnotationModified failed:", e);
          }
        }

        // Sync 3D viewer after bump edits
        if (hasNnInteractiveSession()) {
          refreshViewerFromAnnotations();
        }
      } catch (e) {
        // Warn (not debug) so production traces surface mouseUp failures —
        // off-plane polyline persistence is silent without this signal.
        logger.warn("[BumpContourTool] mouseUpCallback failed:", e);
      }
    }
  }

  return BumpContourTool;
}
